import { GoToDialogHelper } from "csi/middleware/goToDialog/GoToDialogHelper";
import { C8Node, DUMMY, cnl } from "csi/typings";
import { isDefined, isNullOrNaN } from "csi/utility/coreUtility";
import xml from "csi/xml";
import { green, grey, red } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { orderBy } from "lodash";
import { ObservableMap, action, computed, makeObservable, observable } from "mobx";
import { SyntheticEvent } from "react";
import { AbstractMaterial, BaseCompositionMaterial, CompositionMaterialDetails } from "./BaseCompositionMaterial";
import { BaseCompositionStore, createBOMMaterialKey } from "./BaseCompositionStore";
import { BomMaterialDetails, MaterialOnBOM } from "./MaterialOnBOM";
import { SalesRegionException } from "./SalesRegionException";

export interface PlacementDetails {
	url: cnl;
	materials: BomMaterialDetails[] | CompositionMaterialDetails[];
}

export class CompositionPlacement extends AbstractMaterial {
	@observable private _sortValue: number;
	materialBOMMap: ObservableMap<string, MaterialOnBOM> = observable.map<string, MaterialOnBOM>(null, { deep: false });
	placementGoToDialogHelper: GoToDialogHelper;
	bomMaterialGoToDialogHelper: GoToDialogHelper;
	placementItemsForDropdown: ComponentItem[];

	/** percentage of a composition if the user does not have sustainability */
	get percentage(): number {
		const { baseCompositionMaterials, materialBOMs } = this;
		let amount = 0;

		if (this.baseCompositionStore.fromStyleBom) {
			materialBOMs.forEach((material) => {
				if (isDefined(material.percentage) && material.percentage > 0) {
					amount += material.percentage;
				}
			});
		} else {
			baseCompositionMaterials.forEach((composition) => {
				if (isDefined(composition.percentage) && composition.percentage > 0) {
					amount += composition.percentage;
				}
			});
		}

		return amount;
	}

	/** Checks if the current placement contains a placeholder URL marked as 'DUMMY' */
	get containsDUMMY(): boolean {
		return this.baseCompositionStore.fromStyleBom
			? this.materialBOMs.some((material) => material.currentUrl === DUMMY)
			: this.baseCompositionMaterials.some((composition) => composition.currentUrl === DUMMY);
	}

	/** Checks if any BOM material in this placement lacks a composition */
	get hasBOMMaterialWithoutComposition(): boolean {
		return this.materialBOMs.some((material) => {
			return material.baseCompositionMaterials.length === 0 || material.baseCompositionMaterials.some((composition) => composition.currentUrl === DUMMY);
		});
	}

	/** Checks if any composition material in this placement has null percentage */
	get hasNullPercentageCompositionMaterial(): boolean {
		return this.baseCompositionStore.fromStyleBom
			? this.materialBOMs.some((material) => material.baseCompositionMaterials.some((composition) => isNullOrNaN(composition.rawPercentage)))
			: this.baseCompositionMaterials.some((composition) => isNullOrNaN(composition.rawPercentage));
	}

	/** Checks if any composition material in this placement has zero percentage */
	get hasZeroPercentageCompositionMaterial(): boolean {
		return this.hasNullPercentageCompositionMaterial || (this.baseCompositionStore.fromStyleBom
			? this.materialBOMs.some((material) => material.baseCompositionMaterials.some((composition) => composition.percentage === 0))
			: this.baseCompositionMaterials.some((composition) => composition.percentage === 0));
	}

	/** Checks if any BOM material in this placement has zero percentage */
	get hasZeroPercentageBOMMaterial(): boolean {
		return this.hasNullPercentageBOMMaterial || this.materialBOMs.some((material) => material.percentage === 0);
	}

	/** Checks if any BOM material in this placement has null percentage */
	get hasNullPercentageBOMMaterial(): boolean {
		return this.materialBOMs.some((material) => isNullOrNaN(material.rawPercentage));
	}

	/** percentage of a composition if the user does not have sustainability */
	get sortValue(): number {
		return this._sortValue;
	}

	/** Computes an array of BaseCompositionMaterial objects from the baseCompositionMaterialMap. */
	@computed get materialBOMs(): MaterialOnBOM[] {
		return Array.from(this.materialBOMMap.values());
	}

	/** Adds a new BaseCompositionMaterial instance to the baseCompositionMaterialMap. */
	@action addBOMMaterial = (): void => {
		const key = createBOMMaterialKey();
		this.materialBOMMap.set(key, new MaterialOnBOM(this.baseCompositionStore, key));
	};

	/** Adds a new BaseCompositionMaterial instance to the baseCompositionMaterialMap. */
	@action addBOMMaterialWithPercentageAndUrl = (percentage: number, url: cnl, fromGoToDialog: boolean): string => {
		const key = createBOMMaterialKey();
		const bom = new MaterialOnBOM(this.baseCompositionStore, key, percentage, url);
		this.materialBOMMap.set(key, bom);
		if (fromGoToDialog) {
			bom.endSetUrlProcess();
		}
		return key;
	};

	/** Removes a BaseCompositionMaterial instance from the baseCompositionMaterialMap. */
	@action removeBOMMaterial = (key: string): void => {
		this.materialBOMMap.delete(key);
	};

	/** Sets the current URL for a CompositionPlacement based on a selected ComponentItem. */
	@action setSortValue = (sortValue: number): void => {
		this._sortValue = sortValue;
	};

	get currentPlacementNode(): C8Node {
		return this.baseCompositionStore.placementNodes.find((placement) => placement.$URL === this.currentUrl);
	}

	/** indicate if percentage is valid or not */
	get percentageIsInvalid(): boolean {
		return !!this.percentage && this.percentage !== 100;
	}

	/** indicate if percentage is valid or not */
	get percentageFieldHighlightRed(): boolean {
		return this.baseCompositionStore.enforceHundredPercent && !!this.percentage && this.percentage !== 100;
	}

	/** display hint color based on disableSave */
	get placementPercentageBackground(): string {
		if (!isDefined(this.percentage) || this.percentage === 0) {
			return grey[100];
		}
		return this.percentageIsInvalid ? red[50] : green[50];
	};

	get rightDrawerPlacementText(): string {
		return this.baseCompositionStore.loc.getNodeName(this.currentPlacementNode);
	}

	/** Retrieves a set of used BOMMaterials, excluding DUMMY */
	get usedBOMMaterialWithoutDummy(): Set<cnl> {
		const usedMaterialsSet = new Set(this.materialBOMs
			.map(({ currentUrl }) => currentUrl)
			.filter((url) => url !== "DUMMY")); // Filtering out 'DUMMY' before creating the Set
		return usedMaterialsSet;
	}

	@computed get placementDetails(): PlacementDetails {
		const { baseCompositionMaterials, baseCompositionStore, currentUrl, materialBOMs } = this;
		const materials = baseCompositionStore.fromStyleBom ?
			materialBOMs.map((material) => material.bomMaterialDetails) :
			baseCompositionMaterials.map((composition) => composition.compositionMaterialDetails);

		return {
			url: currentUrl,
			materials,
		};
	}

	/** Retrieves an array of ComponentItems suitable for use in a dropdown menu, excluding invalid items. */
	getBomMaterialItemsForDropdown = (index: number): ComponentItem[] => {
		const { currentUrl } = this.materialBOMs[index];
		if (currentUrl === DUMMY) {
			return this.materialsAsComponentItems.filter((item) => (!this.usedBOMMaterialWithoutDummy.has(item.id) && !item.invalid));
		}
		const currentItem = this.findMaterialItem(currentUrl);
		return [
			currentItem,
			...this.materialsAsComponentItems.filter((item) => (!this.usedBOMMaterialWithoutDummy.has(item.id) && !item.invalid)),
		];
	};

	/** gets the same baseCompositionMaterials, but sorts them by percentage in desc order and names in asc order */
	getSortedBaseCompositionMaterials = (salesRegionException: SalesRegionException): BaseCompositionMaterial[] => {
		const filteredMaterials = this.baseCompositionStore.fromStyleBom
			? this.materialBOMs.flatMap((materialBOM) =>
				materialBOM.baseCompositionMaterials.map((material) =>
					new BaseCompositionMaterial(
						this,
						material.key,
						!materialBOM.percentage ? materialBOM.rawPercentage : material.percentage ? material.percentage * materialBOM.percentage * 0.01 : material.rawPercentage,
						material.currentUrl,
						material.currentType,
						material.nestUnderContainsPlacement,
					)))
			: [...this.baseCompositionMaterials];

		const combinedMap = new Map<string, BaseCompositionMaterial>();

		// this part allows the combineSameCompositionMaterials boolean to combine same types
		filteredMaterials.forEach((material) => {
			if (material.currentUrl === DUMMY) return;
			const key = salesRegionException.combineSameCompositionMaterials && !material.nestUnderContainsPlacement
				? (salesRegionException.includeCompositionTypes ? `${material.currentMaterialName}-${material.currentMaterialTypeName}` : material.currentMaterialName)
				: material.key; 

			const existingMaterial = combinedMap.get(key);
			if (existingMaterial) {
				combinedMap.set(key, new BaseCompositionMaterial(
					this,
					existingMaterial.key,
					(existingMaterial.percentage ? existingMaterial.percentage : 0) + (material.percentage ? material.percentage : 0),
					existingMaterial.currentUrl,
					existingMaterial.currentType,
					existingMaterial.nestUnderContainsPlacement,
				));
			} else {
				combinedMap.set(key, material);
			}
		});

		// Sort the materials
		return orderBy(Array.from(combinedMap.values()), [
			(material): number => isDefined(material.percentage) ? -material.percentage : null, // desc
			(material): string => material.currentMaterialName.toLocaleLowerCase(), // asc
			(material): string => material.currentMaterialTypeName.toLocaleLowerCase(), // asc
		]);
	};

	/** goToDialog onClick that opens a list of the currently selectable CompositionPlacements */
	onClickPlacementGoToDialog = (e: SyntheticEvent): void => {
		const { baseCompositionStore, currentPlacementNode, currentUrl, placementGoToDialogHelper } = this;
		const selections = currentPlacementNode ? [currentPlacementNode] : [];
		const placementCNLs = this.placementItemsForDropdown.map((placementItem) => placementItem.id);
		if (currentUrl !== DUMMY) {
			placementCNLs.push(currentUrl);
		}
		placementGoToDialogHelper.openGoToDialogWithCNLs(placementCNLs, selections);
	};

	/** goToDialog onClick that opens a list of the currently selectable BOMMaterials */
	onClickBOMMaterialGoToDialog = (e: SyntheticEvent): void => {
		const { baseCompositionStore, materialBOMs, materialsAsComponentItems, currentUrl, bomMaterialGoToDialogHelper } = this;
		const materialBOMURLs = materialBOMs.length > 0 ? materialBOMs.map((bom) => bom.currentUrl) : [];
		const materialBOMSelections = baseCompositionStore.materialNodes.filter((node) => materialBOMURLs.includes(node.$URL));
		const materialCNLs = materialsAsComponentItems.map((placementItem) => placementItem.id);
		if (currentUrl !== DUMMY) {
			materialCNLs.push(currentUrl);
		}
		bomMaterialGoToDialogHelper.openGoToDialogWithCNLs(materialCNLs, materialBOMSelections);
	};

	findMaterialItem = (url: cnl): ComponentItem => {
		let item = this.materialsAsComponentItems.find((item) => item.id === url);
		if (!item) {
			let node = this.baseCompositionStore.cache.get(url);
			if (!node) {
				node = this.baseCompositionStore.existingBaseMaterialNodes.find((node) => node.MaterialFromBOM === url);
			}
			item = {
				id: xml.nodeGetURL(node),
				invalid: true,
				node,
			};
		}
		return item;
	};

	/** Gets the current dropdown value for a given CompositionPlacementType. */
	getCurrentMaterialBOMDropdownValue = (material: MaterialOnBOM): ComponentItem | null => {
		const { currentUrl } = material;
		if (currentUrl === DUMMY) {
			return null;
		}
		const item = this.findMaterialItem(currentUrl);
		return item;
	};

	constructor(baseCompositionStore: BaseCompositionStore, compositionKey: string, sortValue: number, placementItemsForDropdown: ComponentItem[], currentUrl?: cnl) {
		super(baseCompositionStore, compositionKey, 0, currentUrl);
		makeObservable(this);
		this._sortValue = sortValue;
		this.placementItemsForDropdown = placementItemsForDropdown;
		this.placementGoToDialogHelper = new GoToDialogHelper(
			"CompositionPlacement",
			false,
			action((selectedUrl: cnl[]) => {
				this._currentUrl = selectedUrl[0];
			}),
		);
		this.bomMaterialGoToDialogHelper = new GoToDialogHelper(
			"Material",
			true,
			(selectedUrls: cnl[]) => {
				const existingMaterials = this.materialBOMs.map((bom) => bom.currentUrl);

				this.baseCompositionStore.focusDropDownBoolean = false;
				// Removing unselected materials
				this.materialBOMs
					.filter((bom) => !selectedUrls.includes(bom.currentUrl))
					.forEach((bom) => this.removeBOMMaterial(bom.key));

				// Adding new materials
				selectedUrls
					.filter((url) => !existingMaterials.includes(url))
					.forEach((url) => this.addBOMMaterialWithPercentageAndUrl(null, url, true));
			},
		);
	}
}